<template>
  <b-row>
    <section class="w-100" v-if="itemLoaded">
      <!-- Header -->
      <div class="header">
        <div class="header-body">
          <div class="row align-items-end">
            <div class="col">
              <h1 class="header-title">Order Return</h1>
            </div>
            <div class="col-auto">
              <div class="row align-items-center">
                <div class="col-auto" v-if="itemForm.status">
                  <span class="small">Status</span>
                  <h5><OrderReturnStatusBadge :status="itemForm.status" /></h5>
                </div>
                <div class="col-auto" v-if="itemForm.createdAt">
                  <span class="small">Created At</span>
                  <h5>{{ itemForm.createdAt }}</h5>
                </div>
                <div class="col-auto" v-if="itemForm.warehouseName">
                  <span class="small">Warehouse</span>
                  <h5>{{ itemForm.warehouseName }}</h5>
                </div>
                <div class="col-auto" v-if="itemForm.orderNumber">
                  <span class="small">Order Number</span>
                  <h5>
                    {{ itemForm.orderNumber }}
                    <router-link
                      v-if="itemForm.orderId"
                      :to="{ name: 'Common.OrderDetails', query: { id: itemForm.orderId } }"
                      class="ml-1 text-muted"
                      v-b-tooltip.hover
                      title="View Order"
                    >
                      <i class="fa fa-external-link"></i>
                    </router-link>
                  </h5>
                </div>
                <div class="col-auto" v-if="itemForm.referenceNum">
                  <span class="small">Return Ref #</span>
                  <h5>{{ itemForm.referenceNum }}</h5>
                </div>
                <div class="col-auto" v-if="itemForm.receiveDate">
                  <span class="small">Receive Date</span>
                  <h5>{{ itemForm.receiveDate }}</h5>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="row px-3">
        <div class="col-12 col-sm-9">
          <!-- Notes -->
          <b-card class="mb-3" v-if="itemForm.notes">
            <b-form-group label="Notes">
              <b-form-textarea :value="itemForm.notes" :disabled="true" rows="3" />
            </b-form-group>
          </b-card>

          <!-- Items -->
          <b-card no-body v-if="itemForm.items && itemForm.items.length > 0">
            <template v-slot:header>
              <h4 class="card-header-title">Items Returned</h4>
            </template>
            <div class="return-items" data-testid="return-items">
              <b-card no-body class="return-item" v-for="(item, idx) in itemForm.items" :key="(item.id || item.sku) + '-' + idx">
                <div class="card-body row mx-0">
                  <div class="col-12 col-lg-5 mb-3 mb-lg-0">
                    <div class="mb-1">{{ item.name }}</div>
                    <div class="font-weight-bold mb-1"><CopyText :text="item.sku" /></div>
                    <div class="text-muted small mt-1" v-if="item.returnReason"><b>Customer reason: </b>{{ item.returnReason }}</div>
                    <div class="text-muted small mt-1" v-if="item.lotNumber"><b>Lot #:</b> {{ item.lotNumber }}</div>
                    <div class="text-muted small mt-1" v-if="item.expirationDate"><b>Expires On:</b> {{ item.expirationDate | moment('MMMM Do YYYY') }}</div>
                    <div class="text-muted small mt-1" v-if="item.parentSku"><b>Component of Bundle</b>: {{ item.parentSku }}</div>
                  </div>

                  <div class="col-4 col-lg-2">
                    <span class="return-item-label">Returned</span>
                    <span class="h5 mb-0">{{ item.quantityToReturn }}</span>
                  </div>

                  <div class="col-4 col-lg-2">
                    <span class="return-item-label">Restocked</span>
                    <span class="h5 mb-0">{{ item.quantityReceived === null || item.quantityReceived === undefined || item.quantityReceived === '' ? '-' : item.quantityReceived }}</span>
                  </div>

                  <div class="col-4 col-lg-3">
                    <span class="return-item-label">Rejected</span>
                    <span class="h5 mb-0">{{ item.quantityDamaged === null || item.quantityDamaged === undefined || item.quantityDamaged === '' ? '-' : item.quantityDamaged }}</span>
                    <template v-if="item.itemReviewState && item.itemReviewState !== 'restockable'">
                      <span class="text-muted mx-1">-</span>
                      <b-badge variant="danger">
                        {{ ReviewStateLabel(item.itemReviewState) }}
                      </b-badge>
                      <div class="text-muted review-description" v-if="item.itemReviewState === 'other' && item.itemReviewDescription">
                        {{ item.itemReviewDescription }}
                      </div>
                    </template>
                  </div>
                </div>
              </b-card>
            </div>
          </b-card>
        </div>

        <!-- Sidebar -->
        <aside class="col-12 col-sm-3">
          <b-card header="Actions">
            <router-link
              v-if="itemForm.id && canEditOrderReturn(itemForm) && !has('pm.is_end_partner')"
              class="mb-2 btn btn-block btn-primary"
              :to="{ name: 'Storefronts.OrderReturns.Form', params: { orderReturnId: itemForm.id } }"
            >
              Update Order Return
            </router-link>
            <b-button
              block
              variant="outline-danger"
              v-if="itemForm.id && has('pm.customerBilling.billingEntries')"
              @click="showClientBillingLedger = true"
            >
              Client Billing Ledger
            </b-button>
            <ClientBillingLedgerSidebar
              v-model="showClientBillingLedger"
              :related-id="itemForm.id"
              related-type="OrderReturn"
            />
            <router-link
              class="mt-2 btn btn-block btn-outline-secondary"
              :to="{ name: 'Storefronts.OrderReturns.Index' }"
              exact
            >
              Back To List
            </router-link>
            <b-form-group label="Connect Log" class="mt-3" v-if="itemForm.connectLog">
              <b-form-textarea
                :value="itemForm.connectLog"
                :disabled="true"
                rows="5"
                max-rows="12"
              />
            </b-form-group>
          </b-card>
          <!-- Ship From - To -->
          <b-card header="Ship From - To" class="mt-3" v-if="addressBlocks.length > 0 || (itemForm.events && itemForm.events.length > 0)">
            <div class="mb-3" v-if="itemForm.events && itemForm.events.length > 0" data-testid="return-events">
              <TrackingEvents :events="itemForm.events" title="Tracking Events" />
            </div>
            <template v-for="(block, idx) in addressBlocks">
              <div class="text-center text-muted my-2" v-if="idx > 0" :key="'arrow-' + block.key">
                <i class="fa fa-long-arrow-down fa-lg"></i>
              </div>
              <div class="address-block" :key="block.key" :data-testid="'return-address-' + block.key">
                <div class="address-label">{{ block.label }}</div>
                <div class="font-weight-bold" v-if="block.address.name">{{ block.address.name }}</div>
                <div v-if="block.address.company">{{ block.address.company }}</div>
                <div class="text-muted small mt-1" v-if="block.address.street1">{{ block.address.street1 }}</div>
                <div class="text-muted small" v-if="block.address.street2">{{ block.address.street2 }}</div>
                <div class="text-muted small" v-if="block.address.city || block.address.state || block.address.postalCode">
                  {{ [block.address.city, block.address.state, block.address.postalCode].filter(Boolean).join(', ') }}
                </div>
                <div class="text-muted small" v-if="block.address.country">{{ block.address.country }}</div>
                <div class="small mt-2" v-if="block.address.phone">
                  <i class="fa fa-phone text-muted mr-1"></i>{{ block.address.phone }}
                </div>
              </div>
            </template>
          </b-card>

          <!-- Dimensions & Weight -->
          <b-card header="Dimensions &amp; Weight" class="mt-3" v-if="itemForm.weight || itemForm.dimensions">
            <b-form-group label="Weight" v-if="itemForm.weight && itemForm.weight.value">
              <b-form-input :value="itemForm.weight.value + ' ' + (itemForm.weight.units || 'lbs')" :disabled="true" />
            </b-form-group>
            <b-form-group label="Length" v-if="itemForm.dimensions && itemForm.dimensions.length">
              <b-form-input :value="itemForm.dimensions.length + ' in'" :disabled="true" />
            </b-form-group>
            <b-form-group label="Width" v-if="itemForm.dimensions && itemForm.dimensions.width">
              <b-form-input :value="itemForm.dimensions.width + ' in'" :disabled="true" />
            </b-form-group>
            <b-form-group label="Height" v-if="itemForm.dimensions && itemForm.dimensions.height">
              <b-form-input :value="itemForm.dimensions.height + ' in'" :disabled="true" />
            </b-form-group>
          </b-card>

          <!-- Return Label -->
          <b-card no-body class="mt-3" v-if="itemForm.shipmentLabel">
            <template #header>
              <div class="w-100 d-flex justify-content-between align-items-center">
                <span>Return Label</span>
                <a
                  v-if="itemForm.shipmentLabel.labelUrl"
                  :href="itemForm.shipmentLabel.labelUrl"
                  target="_blank"
                  class="btn btn-sm btn-outline-primary label-view-btn"
                  v-b-tooltip.hover
                  title="Open return label"
                  data-testid="return-label-view"
                >
                  <svg class="label-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                    <circle cx="12" cy="12" r="3" />
                  </svg>
                  View
                </a>
              </div>
            </template>
            <div class="card-body">
              <div class="label-field" v-if="itemForm.shipmentLabel.trackingNumber">
                <div class="label-field-title">
                  <svg class="label-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M16.5 9.4l-9-5.19M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
                    <polyline points="3.27 6.96 12 12.01 20.73 6.96" />
                    <line x1="12" y1="22.08" x2="12" y2="12" />
                  </svg>
                  Tracking Number
                </div>
                <div class="font-weight-bold">
                  <CopyText :text="itemForm.shipmentLabel.trackingNumber" />
                </div>
              </div>

              <div class="label-field" v-if="itemForm.shipmentLabel.carrier">
                <div class="label-field-title">
                  <svg class="label-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <rect x="1" y="3" width="15" height="13" />
                    <polygon points="16 8 20 8 23 11 23 16 16 16 16 8" />
                    <circle cx="5.5" cy="18.5" r="2.5" />
                    <circle cx="18.5" cy="18.5" r="2.5" />
                  </svg>
                  Carrier
                </div>
                <div>{{ itemForm.shipmentLabel.carrier }}</div>
              </div>

              <div class="label-field" v-if="itemForm.shipmentLabel.service">
                <div class="label-field-title">
                  <svg class="label-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
                  </svg>
                  Service
                </div>
                <div>{{ itemForm.shipmentLabel.service }}</div>
              </div>

              <div class="label-field mb-0" v-if="itemForm.shipmentLabel.labelUrl">
                <div class="label-field-title">
                  <svg class="label-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                    <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
                  </svg>
                  Label URL
                </div>
                <b-input-group size="sm">
                  <b-form-input :value="itemForm.shipmentLabel.labelUrl" :readonly="true" />
                  <b-input-group-append>
                    <b-input-group-text v-b-tooltip.hover title="Copy label URL">
                      <CopyText :text="itemForm.shipmentLabel.labelUrl"><span></span></CopyText>
                    </b-input-group-text>
                  </b-input-group-append>
                </b-input-group>
              </div>
            </div>
          </b-card>
        </aside>
      </div>
    </section>

    <div v-else class="w-100 text-center p-5">
      <b-spinner></b-spinner>
      <p class="mt-2">Loading return...</p>
    </div>
  </b-row>
</template>

<script>
import { crudMixinGen, myCommonDataMixin } from '@/helpers/utils/vueMixins.js';
import ClientBillingLedgerSidebar from '../../../components/ClientBillingLedgerSidebar.vue';
import OrderReturnStatusBadge from '../../../components/OrderReturnStatusBadge.vue';

export default {
  name: 'Storefronts.OrderReturns.View',
  mixins: [crudMixinGen('returns'), myCommonDataMixin],
  components: { ClientBillingLedgerSidebar, OrderReturnStatusBadge },

  props: {
    orderReturnId: {
      type: String,
      default: '',
      required: false
    }
  },

  data: function() {
    return {
      showClientBillingLedger: false,
      itemLoaded: false,

      reviewStates: [
        { value: 'restockable', text: 'Restockable' },
        { value: 'damaged', text: 'Damaged' },
        { value: 'item-worn-out', text: 'Item worn out' },
        { value: 'used-or-opened', text: 'Used / opened' },
        { value: 'missing-parts', text: 'Missing parts' },
        { value: 'expired', text: 'Expired' },
        { value: 'wrong-item', text: 'Wrong item returned' },
        { value: 'not-as-described', text: 'Not as described' },
        { value: 'packaging-damaged', text: 'Packaging damaged' },
        { value: 'other', text: 'Other' }
      ]
    };
  },

  computed: {
    addressBlocks() {
      var blocks = [];
      if (this.itemForm && this.itemForm.originAddress) {
        blocks.push({ key: 'from', label: 'From (Customer)', address: this.itemForm.originAddress });
      }
      if (this.itemForm && this.itemForm.returnAddress) {
        blocks.push({ key: 'to', label: 'To', address: this.itemForm.returnAddress });
      }
      return blocks;
    }
  },

  mounted: function() {
    this.ResetForm();
    if (this.orderReturnId) {
      this.LoadOrderReturn();
    }
  },

  methods: {
    ReviewStateLabel(value) {
      var match = this.reviewStates.find(function(o) { return o.value === value; });
      return match ? match.text : value;
    },
    canEditOrderReturn(orderReturn)
    {
      let canEdit = true;
      if(orderReturn.status == 'Received' || orderReturn.status == 'Cancelled' || orderReturn.status == 'ReceivedDamaged')
      {
        canEdit = false;
      }
      return canEdit
    },
    LoadOrderReturn: function() {
      var self = this;
      this.$http
        .get('/api/common/orderReturns/' + this.orderReturnId)
        .then(function(resp) {
          self.itemForm = resp.data;

          if (self.itemForm.createDateTimeUtc) {
            self.itemForm.createdAt = new Date(self.itemForm.createDateTimeUtc)
              .toISOString()
              .split('T')[0];
          }

          if (self.itemForm.receiveDate) {
            self.itemForm.receiveDate = new Date(self.itemForm.receiveDate)
              .toISOString()
              .split('T')[0];
          }

          self.itemLoaded = true;
        })
        .catch(function(err) {
          console.error('LoadOrderReturn', err);
          self.$swal('Error', 'Failed to load order return.', 'error');
        });
    }
  }
};
</script>

<style scoped>
.return-items {
  padding: 1rem 1.25rem 0;
}
.return-item {
  margin-bottom: 1rem;
  border-radius: 0.5rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
}
.return-item .card-header {
  padding: 0.4rem 1rem;
}
.return-item .card-footer {
  border-bottom-left-radius: 0.5rem;
  border-bottom-right-radius: 0.5rem;
}
.label-view-btn {
  padding: 0.2rem 0.5rem;
  font-size: 0.7rem;
  line-height: 1.4;
}
.label-view-btn .label-icon {
  width: 11px;
  height: 11px;
}
.label-icon {
  width: 14px;
  height: 14px;
  vertical-align: -2px;
}
.label-field {
  margin-bottom: 1rem;
}
.label-field-title {
  margin-bottom: 0.25rem;
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #73818f;
}
.review-description {
  margin-top: 0.25rem;
  font-size: 0.75rem;
}
.address-block {
  padding: 0.5rem 0.75rem;
  border-left: 3px solid #c8ced3;
  background: #f8f9fa;
  border-radius: 0.25rem;
}
.address-label {
  margin-bottom: 0.25rem;
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #73818f;
}
.return-item-label {
  display: block;
  margin-bottom: 0.25rem;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: #73818f;
}
</style>
